import React, { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Row, Col, Button } from 'antd';

import {
  getCurrency,
  getInstrumentsAll,
} from '../../data/store/instruments/instrumentsActions';

import FooterHoldings from '../../pages/ClientOverview/FooterHoldings/FooterHoldings';

import {numberWithSpacesInter} from '../../helpers/formatInteger';
import { twoDecimalWithSpaces } from '../../helpers/formatFractional';
import { decimalSeparator } from '../../helpers/decimalSeparator';
import { textSort } from '../../helpers/textSort';

import CustomTable from '../../components/CustomTable/CustomTable';
import CustomSortIcon from '../../components/CustomSortIcon/CustomSortIcon';
import PortfolioListComposite from './PortfolioListComposite';
import './style.scss';

const PortfolioCompose = ({
  totalValue,
  data,
  dataUnlisted,
  dataCashes,
  userId,
  portfolioId,
  setSaveAlert,
  selectedKeys, 
  setSelectedKeys
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('');

  const [tabs, setTabs] = useState(3);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableColumnsUnlisted, setTableColumnsUnlisted] = useState([]);
  const [tableColumnsCashes, setTableColumnsCashes] = useState([]);

 

  const {
    portfolioById,
  } = useSelector((state) => state.portfolioReducer);
  const instrumentsList = useSelector(
    (state) => state.instrumentsReducer?.withoutCashes?.list,
  );
  const cashesList = useSelector(
    (state) => state.instrumentsReducer.cashes.list,
  );
  console.log('🚀 ~ PortfolioCompose ~ cashesList:', cashesList);
  const { currencyList, assets } = useSelector(
    (state) => state.instrumentsReducer,
  );

  const processedDataCashes = useMemo(() => {
    if (!dataCashes || !Array.isArray(dataCashes)) {
      return [];
    }

    const grouped = dataCashes.reduce((acc, item) => {
      const instrumentId = item.instrument_id;
      
      if (acc[instrumentId]) {
        acc[instrumentId].amount += item.amount;
      } else {
        acc[instrumentId] = {
          portfolio_id: item.portfolio_id,
          currency_id: item.currency_id,
          instrument_id: item.instrument_id,
          amount: item.amount,
          current_weight_fin: item.current_weight_fin,
          current_weight: item.current_weight,
        };
      }
      
      return acc;
    }, {});

    return Object.values(grouped);
  }, [dataCashes]);

  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    const grouped = data.reduce((acc, item) => {
      const instrumentId = item.financial_instrument_id;
      
      if (acc[instrumentId]) {
        acc[instrumentId].units_number += item.units_number || 0;
        acc[instrumentId].value += item.value || 0;
      } else {
        // eslint-disable-next-line no-unused-vars
        const { id, ...rest } = item;
        acc[instrumentId] = {
          ...rest,
          units_number: item.units_number || 0,
          value: item.value || 0,
        };
      }
      
      return acc;
    }, {});
  
    return Object.values(grouped);
  }, [data]);

  const getCurrencyName = (id) => {
    const name = currencyList?.find((i) => i.id === id);
    return name?.name;
  };
  const getCashName = (id) => {
    const cash = cashesList?.find((i) => i.ID === id);
    return cash?.name;
  };

  useEffect(() => {
    setSaveAlert({ flag: false, path: '' });
    dispatch(getInstrumentsAll({ limit: -1, offset: 0, type: 'cashes-only' }));
    dispatch(
      getInstrumentsAll({ limit: -1, offset: 0, type: 'without-cashes' }),
    );
    dispatch(getCurrency());
  }, []);

  const getRate = (cashCurency) => {
    const rate = currencyList?.find((i) => i.id === cashCurency)?.rate;
    return userId ? rate : '0';
  };

  const getInstrument = (id) => {
    const instrument = instrumentsList?.find((i) => i.ID === id);
    return instrument;
  };

  useEffect(() => {
    const columns = [
      {
        title: t('INSTRUMENT'),
        dataIndex: 'instrument_name',
        width: '27%',
        editable: true,
        sorter: (a, b) => textSort(a.instrument_name, b.instrument_name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('ISIN'),
        dataIndex: 'isin',
        width: '7%',
        sorter: (a, b) => textSort(a.isin, b.isin),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('UNITS'),
        dataIndex: 'units_number',
        align: 'right',
        width: '14%',
        editable: true,
        render: (value, record) => {
          return !record.is_pending
            ? value
              ? numberWithSpacesInter(value, null, 4)
              : ''
            : '- - -';
        },
        sorter: (a, b) => a.units_number - b.units_number,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('SHARE_PRICE'),
        dataIndex: 'latest_value',
        align: 'right',
        width: '13%',
        editable: true,
        render: (value, record) => {
          return value
            ? `${numberWithSpacesInter(value, 2)} ${getCurrencyName(record.currency_id)}`
            : `${numberWithSpacesInter(getInstrument(record?.financial_instrument_id)?.latest_value, 2)} ${getCurrencyName(getInstrument(record?.financial_instrument_id)?.currency_id)}`;
        },
        sorter: (a, b) => a.latest_value - b.latest_value,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('VALUE') + ' kr',
        dataIndex: 'value',
        align: 'right',
        width: '13%',
        render: (value) => {
          return `${numberWithSpacesInter(value, 0)}`;
        },
        sorter: (a, b) => a.value - b.value,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('FEE'),
        dataIndex: 'fee',
        className: 'instrument-fee',
        width: '7%',
        render: (value) => {
          return `${value?.toString()?.replace('.', decimalSeparator())} %`;
        },
        sorter: (a, b) => a.fee - b.fee,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('ASSET_CLASS'),
        dataIndex: 'asset_class',
        width: '17%',
        render: (value, record) => {
          if (value !== 'TBD') {
            return record.financial_asset?.name;
          }
          return '- - -';
        },
        sorter: (a, b) => textSort(a.asset_class, b.asset_class),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
    ];
    const getAssetClass = (value) => {
      const classArray = assets
        .filter((i) => [7, 8, 15, 16].includes(i.ID))
        .map((el) => [el.ID, el.name]);
      const classObj = Object.fromEntries(classArray);

      return classObj[value];
    };
    const columnsUnlisted = [
      {
        title: t('INSTRUMENT'),
        dataIndex: 'name',
        width: '30%',
        editable: true,
        sorter: (a, b) => textSort(a.name, b.name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('UNITS'),
        dataIndex: 'units_number',
        align: 'right',
        width: '15%',
        editable: true,
        render: (value) => {
          return numberWithSpacesInter(value);
        },
        sorter: (a, b) => a.units_number - b.units_number,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('SHARE_PRICE') + ' kr',
        dataIndex: 'latest_value_sek',
        align: 'right',
        width: '15%',
        editable: true,
        render: (value) => {
          return `${numberWithSpacesInter(value, 2)}`;
        },
        sorter: (a, b) => a.latest_value_sek - b.latest_value_sek,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('VALUE') + ' kr',
        dataIndex: 'value',
        align: 'right',
        width: '15%',
        editable: false,
        render: (value) => {
          return `${numberWithSpacesInter(value, 0)}`;
        },
        sorter: (a, b) => a.value - b.value,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('ASSET_CLASS'),
        dataIndex: 'financial_asset_id',
        width: '23%',
        editable: true,
        render: (value) => {
          return getAssetClass(value);
        },
        sorter: (a, b) => textSort(a.financial_asset_id, b.financial_asset_id),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
    ];

    const columnsCashes = [
      {
        title: t('CURRENCY'),
        dataIndex: 'name',
        width: '30%',
        editable: true,
        render: (value, record) => {
          return record.instrument_id ? getCashName(record.instrument_id) : '';
        },
        sorter: (a, b) => textSort(a.name, b.name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('AMOUNT'),
        dataIndex: 'amount',
        width: '15%',
        editable: true,
        render: (value) => {
          return numberWithSpacesInter(value, 0);
        },
        sorter: (a, b) => a.amount - b.amount,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('FX_RATE'),
        dataIndex: 'rate',
        width: '15%',
        editable: false,
        render: (_, record) => {
          return record.currency_id
            ? `${numberWithSpacesInter(getRate(record.currency_id), 4)} kr`
            : '';
        },
        sorter: (a, b) => getRate(a.currency_id) - getRate(b.currency_id),
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('AMOUNT') + ' kr',
        dataIndex: 'totalAmount',
        align: 'right',
        width: '15%',
        editable: false,
        render: (value, record) => {
          return `${twoDecimalWithSpaces(record.amount * getRate(record.currency_id))}`;
        },
        sorter: (a, b) => a.totalAmount - b.totalAmount,
        sortIcon: (props) => <CustomSortIcon {...props} />,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: t('ASSET_CLASS'),
        dataIndex: 'financial_asset',
        width: '23%',
        editable: false,
        render: () => {
          return 'Liquidity';
        },
        sorter: (a, b) => textSort(a.financial_asset, b.financial_asset),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
    ];
    setTableColumns(columns);
    setTableColumnsUnlisted(columnsUnlisted);
    setTableColumnsCashes(columnsCashes);
  }, [portfolioById, portfolioId, data, dataUnlisted, dataCashes]);

  const handleTabs = (tab) => {
    setTabs(tab);
  };

  // const footerData = useMemo(() => {
  //   return {
  //     return: portfolioById?.return ?? 0,
  //     cvar: portfolioById?.cvar ?? 0,
  //     vol: portfolioById?.vol ?? 0,
  //     total: portfolioById?.total_value ?? 0,
  //   };
  // }, [portfolioById]);

  return (
    <>
      <div className='portfolio-compose-wrapper'>
        <Row justify={'space-between'} style={{ padding: '24px' }}>
          <Col>
            <Button
              type="text"
              className={`portfolio-tabs-btn ${tabs === 3 ? 'selected' : ''}`}
              onClick={() => handleTabs(3)}
            >
              {t('INCLUDED_PORTFOLIOS')}
            </Button>
            <Button
              type="text"
              className={`portfolio-tabs-btn ${tabs === 0 ? 'selected' : ''}`}
              onClick={() => handleTabs(0)}
            >
              {t('CASH')}
            </Button>
            <Button
              type="text"
              className={`portfolio-tabs-btn ${tabs === 1 ? 'selected' : ''}`}
              onClick={() => handleTabs(1)}
            >
              {t('LISTED_HOLDINGS')}
            </Button>
            <Button
              type="text"
              className={`portfolio-tabs-btn ${tabs === 2 ? 'selected' : ''}`}
              onClick={() => handleTabs(2)}
            >
              {t('UNLISTED_HOLDINGS')}
            </Button>
          </Col>
        </Row>
        <div
          style={{ padding: '0' }}
          className="table-inner-wrapper portfolio-info"
        >
          {tabs === 0 && (
            <div>
              <CustomTable
                rowClassName={() => 'custom-row'}
                rowHoverable={false}
                dataSource={processedDataCashes}
                columns={tableColumnsCashes}
                pagination={false}
              />
            </div>
          )}
          {tabs === 1 && (
            <div>
              <CustomTable
                rowClassName={() => 'custom-row'}
                rowHoverable={false}
                dataSource={processedData}
                columns={tableColumns}
                pagination={false}
              />
            </div>
          )}
          {tabs === 2 && (
            <div>
              <CustomTable
                rowClassName={() => 'custom-row'}
                rowHoverable={false}
                dataSource={dataUnlisted}
                columns={tableColumnsUnlisted}
                pagination={false}
              />
            </div>
          )}
          {tabs === 3 && (
            <PortfolioListComposite 
              list={portfolioById?.composite} 
              selectedKeys={selectedKeys} 
              setSelectedKeys={setSelectedKeys} isLoading={false} />
          )}
        </div>
      </div>
      <FooterHoldings width={'100%'} totalOnly={false} data={{total: totalValue}} />
    </>
  );
};

export default PortfolioCompose;

PortfolioCompose.propTypes = {
  userId: PropTypes.string,
  portfolioId: PropTypes.string,
  setNeedSaveHoldings: PropTypes.func,
  saveAlert: PropTypes.object,
  setSaveAlert: PropTypes.func,
  data: PropTypes.array,
  dataUnlisted: PropTypes.array,
  dataCashes: PropTypes.array,
  selectedKeys: PropTypes.array,
  setSelectedKeys: PropTypes.func,
  totalValue: PropTypes.number,
};
